import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = Cookies.get('refreshToken');
        if (refreshToken) {
          const response = await axios.post('/api/auth/refresh', {
            refreshToken,
          });

          const { accessToken } = response.data.tokens;
          Cookies.set('accessToken', accessToken, { expires: 7 });

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        Cookies.remove('accessToken');
        Cookies.remove('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Show error message
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else if (error.message) {
      toast.error(error.message);
    }

    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (userData) => api.put('/auth/profile', userData),
  changePassword: (passwordData) => api.put('/auth/change-password', passwordData),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (tokenData) => api.post('/auth/reset-password', tokenData),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  updateSettings: (settings) => api.put('/auth/settings', settings),
};

// Orders API calls
export const ordersAPI = {
  getOrders: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return api.get(`/orders?${searchParams.toString()}`);
  },
  deleteOrder: (id) => api.delete(`/orders/${id}`),
  getOrder: (id) => api.get(`/orders/${id}`),
  updateOrderStatus: (id, status, notes) => api.put(`/orders/${id}/status`, { status, notes }),
  updatePaymentStatus: (id, paymentStatus, transactionId, notes) => api.put(`/orders/${id}/payment-status`, { paymentStatus, transactionId, notes }),

  // Public order submission endpoints
  submitOrder: (siteId, orderData) => api.post(`/orders/submit/${siteId}`, orderData),
  submitOrderWithFiles: (siteId, customerData, products, totals, paymentInfo, files) => {
    const data = new FormData();
    data.append('customerData', JSON.stringify(customerData));
    data.append('products', JSON.stringify(products));
    if (totals) data.append('totals', JSON.stringify(totals));
    if (paymentInfo) data.append('paymentInfo', JSON.stringify(paymentInfo));

    // Append files
    if (files && files.length > 0) {
      files.forEach((file) => {
        data.append('files', file);
      });
    }

    return api.post(`/orders/submit/${siteId}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getOrderConfig: (siteId) => api.get(`/orders/test/${siteId}`),
};

// Emails API calls
export const emailsAPI = {
  getEmails: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return api.get(`/emails?${searchParams.toString()}`);
  },
  getEmail: (id) => api.get(`/emails/${id}`),
  deleteEmail: (id) => api.delete(`/emails/${id}`),
  bulkDeleteEmails: (submissionIds) => api.delete('/emails/bulk', { data: { submissionIds } }),
  updateStatus: (id, status, notes) => api.put(`/emails/${id}/status`, { status, notes }),
  getAnalytics: () => api.get('/emails/analytics/overview'),
};

// Websites API calls
export const websitesAPI = {
  getWebsites: () => api.get('/websites'),
  getWebsite: (id) => api.get(`/websites/${id}`),
  createWebsite: (websiteData) => api.post('/websites', websiteData),
  updateWebsite: (id, websiteData) => api.put(`/websites/${id}`, websiteData),
  deleteWebsite: (id) => api.delete(`/websites/${id}`),
  getAnalytics: (id) => api.get(`/websites/${id}/analytics`),
  toggleStatus: (id) => api.post(`/websites/${id}/toggle-status`),
  checkSiteId: (siteId) => api.get(`/websites/check-siteid/${siteId}`),
  publishWebsite: (id) => api.post(`/websites/${id}/publish`),
};

// Forms API calls (public endpoints)
export const formsAPI = {
  submitForm: (siteId, formData) => api.post(`/forms/submit/${siteId}`, { formData }),
  submitFormWithFiles: (siteId, formData, files) => {
    const data = new FormData();
    data.append('formData', JSON.stringify(formData));

    // Append files
    if (files && files.length > 0) {
      files.forEach((file) => {
        data.append('files', file);
      });
    }

    return api.post(`/forms/submit/${siteId}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getFormConfig: (siteId) => api.get(`/forms/test/${siteId}`),
};

// Files API calls
export const filesAPI = {
  getSubmissionFiles: (submissionId) => api.get(`/files/submission/${submissionId}`),
  deleteFile: (fileId) => api.delete(`/files/${fileId}`),
  getWebsiteFiles: (websiteId, params = {}) => api.get(`/files/website/${websiteId}`, { params }),
  downloadFile: (fileId) => `${process.env.REACT_APP_API_URL || '/api'}/files/${fileId}/download`,
};

// Assets API calls
export const assetsAPI = {
  getAssets: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return api.get(`/assets?${searchParams.toString()}`);
  },
  getAnalytics: () => api.get('/assets/analytics'),
  deleteAsset: (id) => api.delete(`/assets/${id}`),
  bulkDeleteAssets: (assetIds) => api.delete('/assets/bulk', { data: { assetIds } }),
};

// Telegram API calls
export const telegramAPI = {
  getConnectionLink: (userId) => api.get(`/telegram/connection-link/${userId}`),
  sendTestMessage: (userId, message) => api.post(`/telegram/send-test-message/${userId}`, { message }),
  getWebhookInfo: () => api.get('/telegram/webhook-info'),
  setWebhook: (url) => api.post('/telegram/set-webhook', { url }),
  deleteWebhook: () => api.delete('/telegram/webhook'),
};

// Dashboard API calls
export const dashboardAPI = {
  getAnalytics: () => api.get('/dashboard/analytics'),
};

// Superadmin API calls
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, updates) => api.put(`/admin/users/${id}`, updates),
  deactivateUser: (id) => api.delete(`/admin/users/${id}`),
  getUserOverview: (id) => api.get(`/admin/users/${id}/overview`),
  getOrdersAnalytics: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    const qs = searchParams.toString();
    return api.get(`/admin/orders/analytics${qs ? `?${qs}` : ''}`);
  },
  getOrdersByWebsite: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    const qs = searchParams.toString();
    return api.get(`/admin/orders/by-website${qs ? `?${qs}` : ''}`);
  }
};

// Superadmin Forms API calls
export const adminFormsAPI = {
  getFormsAnalytics: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    const qs = searchParams.toString();
    return api.get(`/admin/forms/analytics${qs ? `?${qs}` : ''}`);
  },
  getFormsByWebsite: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    const qs = searchParams.toString();
    return api.get(`/admin/forms/by-website${qs ? `?${qs}` : ''}`);
  },
  getForms: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    const qs = searchParams.toString();
    return api.get(`/emails${qs ? `?${qs}` : ''}`);
  },
};

// Schemas API calls
export const schemasAPI = {
  getSchemas: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return api.get(`/schemas?${searchParams.toString()}`);
  },
  getSchema: (id) => api.get(`/schemas/${id}`),
  createSchema: (schemaData) => api.post('/schemas', schemaData),
  updateSchema: (id, schemaData) => api.put(`/schemas/${id}`, schemaData),
  deleteSchema: (id) => api.delete(`/schemas/${id}`),
  toggleStatus: (id) => api.post(`/schemas/${id}/toggle-status`),
  checkSlug: (slug) => api.get(`/schemas/check-slug/${slug}`),
  getSchemaRecords: (id, params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return api.get(`/schemas/${id}/records?${searchParams.toString()}`);
  },
};

// Data API calls
export const dataAPI = {
  getDataRecords: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return api.get(`/data?${searchParams.toString()}`);
  },
  getPublicData: (schemaSlug, params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return api.get(`/data/${schemaSlug}?${searchParams.toString()}`);
  },
  createRecord: (schemaId, recordData) => api.post(`/data/${schemaId}/records`, recordData),
  createRecordsBulk: (schemaId, records) => api.post(`/data/${schemaId}/records/bulk`, { records }),
  updateRecord: (recordId, recordData) => api.put(`/data/records/${recordId}`, recordData),
  deleteRecord: (recordId) => api.delete(`/data/records/${recordId}`),
  bulkDeleteRecords: (recordIds) => api.post('/data/records/bulk-delete', { recordIds }),
  getAnalytics: () => api.get('/data/analytics'),
  getWebsiteData: (websiteId, params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return api.get(`/data/website/${websiteId}?${searchParams.toString()}`);
  },
  getWebsiteDataBySiteId: (siteId, params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return api.get(`/data/site/${siteId}?${searchParams.toString()}`);
  },
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

export default api; 